import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Modal from 'react-modal';

import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    setDoc,
    where,
    arrayUnion,
} from 'firebase/firestore';

import { db } from '../../../firebase.init';

import 'bootstrap/dist/css/bootstrap.min.css';
import './EscalationStrategy.css';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EstimateModal from '../DoorDetails/EstimateModal';
import { useUserRole } from '../../../hooks/useUserRole';

const functions = getFunctions();

const EMPTY_TENANT = {
    name: '',
    phone: '',
    email: '',
    address: '',
    moveInDate: '',
    notes: '',
    escalationHistory: [],
};

const ESCALATION_STEP_OPTIONS = [
    'Pending',
    'Step 1',
    'Step 2',
    'Step 3',
    'Appointment Booked',
    'Not Required',
    'Completed',
];

const normalizeString = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value).trim();
};

const normalizeBoolean = (value) => {
    if (value === false) {
        return false;
    }

    if (typeof value === 'string' && value.trim().toLowerCase() === 'false') {
        return false;
    }

    return true;
};

const parseDate = (value) => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : new Date(value);
    }

    if (typeof value?.toDate === 'function') {
        const parsedDate = value.toDate();

        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (
        typeof value === 'object' &&
        value !== null &&
        typeof value.seconds === 'number'
    ) {
        const parsedDate = new Date(value.seconds * 1000);

        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === 'number') {
        const parsedDate = new Date(value);

        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === 'string') {
        const cleanValue = value.trim();

        if (!cleanValue) {
            return null;
        }

        const ukDateMatch = cleanValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);

        if (ukDateMatch) {
            const [, day, month, year] = ukDateMatch;

            const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

            const isValid =
                parsedDate.getFullYear() === Number(year) &&
                parsedDate.getMonth() === Number(month) - 1 &&
                parsedDate.getDate() === Number(day);

            return isValid ? parsedDate : null;
        }

        const inputDateMatch = cleanValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

        if (inputDateMatch) {
            const [, year, month, day] = inputDateMatch;

            const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));

            const isValid =
                parsedDate.getFullYear() === Number(year) &&
                parsedDate.getMonth() === Number(month) - 1 &&
                parsedDate.getDate() === Number(day);

            return isValid ? parsedDate : null;
        }

        const sqlDateMatch = cleanValue.match(
            /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/,
        );

        if (sqlDateMatch) {
            const [, year, month, day, hours, minutes, seconds = '0'] = sqlDateMatch;

            const parsedDate = new Date(
                Number(year),
                Number(month) - 1,
                Number(day),
                Number(hours),
                Number(minutes),
                Number(seconds),
            );

            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        }

        const parsedDate = new Date(cleanValue);

        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    return null;
};

const startOfDay = (value) => {
    const parsedDate = parseDate(value);

    if (!parsedDate) {
        return null;
    }

    return new Date(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        parsedDate.getDate(),
    );
};

const formatDate = (value) => {
    const parsedDate = parseDate(value);

    if (!parsedDate) {
        return '-';
    }

    return parsedDate.toLocaleDateString('en-GB');
};

const formatDateTime = (value) => {
    const parsedDate = parseDate(value);

    if (!parsedDate) {
        return '-';
    }

    return parsedDate.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatShortDateTime = (value) => {
    const parsedDate = parseDate(value);

    if (!parsedDate) {
        return '-';
    }

    return parsedDate.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatDateForInput = (value) => {
    const parsedDate = parseDate(value);

    if (!parsedDate) {
        return '';
    }

    const year = parsedDate.getFullYear();

    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');

    const day = String(parsedDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

const calculatePercentage = (value, total) => {
    if (!total) {
        return '0%';
    }

    return `${Math.round((value / total) * 100)}%`;
};

const getComparableFlatNumber = (value) => {
    return normalizeString(value)
        .replace(/^R/i, '')
        .replace(/^Flat\s*/i, '')
        .replace(/\s+/g, '')
        .toLowerCase();
};

const getDoorNumber = (door) => {
    const rawDoorNumber =
        normalizeString(door?.doorNumber) ||
        normalizeString(door?.propertyNumber) ||
        normalizeString(door?.flatNumber) ||
        normalizeString(door?.doorId) ||
        normalizeString(door?.id);

    if (!rawDoorNumber) {
        return '-';
    }

    const withoutPrefix = rawDoorNumber.replace(/^R/i, '');

    return `R${withoutPrefix}`;
};

const getDoorInspectionDateValue = (door) => {
    return (
        door?.date ||
        door?.inspectionDate ||
        door?.surveyDate ||
        door?.parentReportDate ||
        door?.sourceReportDate ||
        null
    );
};

const hasCompletedWork = (door) => {
    const workDone = normalizeBoolean(door?.workDone) === true;

    const workDoneUpdatedAt = parseDate(door?.workDoneUpdatedAt);

    return Boolean(workDone && workDoneUpdatedAt);
};

const getAccessStatus = (door) => {
    if (door?.noResidentialRecord) {
        return "Not Surveyed";
    }

    if (hasCompletedWork(door)) {
        return "Work Completed";
    }

    return normalizeBoolean(door?.hasAccess) ? "Access Granted" : "No Access";
};

const getSortedEscalationHistory = (tenantData) => {
    const history = Array.isArray(tenantData?.escalationHistory)
        ? tenantData.escalationHistory
        : [];

    return [...history].sort((entryA, entryB) => {
        const dateA = parseDate(entryA?.date) || new Date(0);

        const dateB = parseDate(entryB?.date) || new Date(0);

        return dateB.getTime() - dateA.getTime();
    });
};

const isEmptyRestartEntry = (entry) => {
    return (
        normalizeString(entry?.step) === 'Step 1' &&
        !normalizeString(entry?.note) &&
        !normalizeString(entry?.action) &&
        !normalizeString(entry?.outcome)
    );
};

const getVisibleEscalationHistory = (history) => {
    return (Array.isArray(history) ? history : []).filter(
        (entry) => !isEmptyRestartEntry(entry),
    );
};

const getLatestEscalationEntry = (tenantData) => {
    const sortedHistory = getSortedEscalationHistory(tenantData);

    return sortedHistory[0] || null;
};

const getAscendingEscalationHistory = (history) => {
    return [...(history || [])].reverse();
};

const findStepTransitionEntry = (history, targetStep) => {
    return (
        [...(history || [])]
            .sort((entryA, entryB) => {
                const dateA = parseDate(entryA?.date) || new Date(0);

                const dateB = parseDate(entryB?.date) || new Date(0);

                return dateB.getTime() - dateA.getTime();
            })
            .find((entry) => entry?.step === targetStep) || null
    );
};

const STEP_SEQUENCE = ['Step 1', 'Step 2', 'Step 3', 'Completed'];

const getHistoryStepLabel = (entry) => {
    const storedHistoryStep = normalizeString(
        entry?.historyStep || entry?.displayStep,
    );

    if (storedHistoryStep) {
        return storedHistoryStep;
    }

    switch (normalizeString(entry?.action)) {
        case 'Initial Contact':
            return 'Step 1';

        case 'Phone Call':
            return 'Step 2';

        case 'Final Letter':
            return 'Step 3 (Completed)';

        default:
            return normalizeString(entry?.step) || 'Update';
    }
};

const getHistoryDotClass = (entry) => {
    const historyStep = getHistoryStepLabel(entry);

    if (historyStep.startsWith('Step 1')) {
        return 'escalation-summary-dot-step1';
    }

    if (historyStep.startsWith('Step 2')) {
        return 'escalation-summary-dot-step2';
    }

    if (historyStep.includes('Completed')) {
        return 'escalation-summary-dot-completed';
    }

    return 'escalation-summary-dot-step3';
};

const getEscalationStep = (
    door,
    tenantData,
    accessStatus,
    workCompleted,
    appointment,
    latestEscalationEntry,
) => {
    const storedTenantStep = normalizeString(
        tenantData?.escalationStep || tenantData?.currentEscalationStep,
    );

    if (storedTenantStep) {
        return storedTenantStep;
    }

    const manualStep = normalizeString(latestEscalationEntry?.step);

    if (manualStep) {
        return manualStep;
    }

    const storedStep = normalizeString(
        door?.escalationStep || door?.currentEscalationStep,
    );

    if (storedStep) {
        return storedStep;
    }

    if (workCompleted || accessStatus === 'Work Completed') {
        return 'Completed';
    }

    if (appointment) {
        return 'Appointment Booked';
    }

    if (accessStatus === 'Access Granted') {
        return 'Not Required';
    }

    if (accessStatus === 'No Access') {
        return 'Step 1';
    }

    return 'Pending';
};

const getNextAction = (
    door,
    accessStatus,
    escalationStep,
    workCompleted,
    appointment,
) => {
    const storedNextAction = normalizeString(door?.nextAction);

    if (storedNextAction) {
        return storedNextAction;
    }

    if (workCompleted || accessStatus === 'Work Completed') {
        return '-';
    }

    if (appointment) {
        return 'Attend appointment';
    }

    if (accessStatus === 'Access Granted') {
        return 'Complete works';
    }

    if (accessStatus === 'No Access') {
        if (escalationStep === 'Step 3') {
            return 'Final escalation';
        }

        if (escalationStep === 'Step 2') {
            return 'Phone follow-up';
        }

        return 'Initial contact';
    }

    return 'Review';
};

const getAppointmentDateValue = (appointment) => {
    if (!appointment) {
        return null;
    }

    return (
        appointment.firstDate ||
        appointment.appointmentDate ||
        appointment.bookingDate ||
        appointment.date ||
        null
    );
};

const isAppointmentAfterDoorDate = (appointment, door) => {
    if (!appointment || !door) {
        return false;
    }

    const appointmentDate = startOfDay(getAppointmentDateValue(appointment));

    const doorDate = startOfDay(getDoorInspectionDateValue(door));

    if (!appointmentDate) {
        return false;
    }

    if (!doorDate) {
        return false;
    }

    return appointmentDate.getTime() > doorDate.getTime();
};

const getLatestValidAppointment = (door, appointmentsByFlat) => {
    const flatKey = getComparableFlatNumber(
        door?.doorNumber ||
        door?.propertyNumber ||
        door?.flatNumber ||
        door?.doorId ||
        door?.id,
    );

    if (!flatKey) {
        return null;
    }

    const flatAppointments = appointmentsByFlat[flatKey];

    if (!Array.isArray(flatAppointments) || flatAppointments.length === 0) {
        return null;
    }

    return (
        flatAppointments.find((appointment) =>
            isAppointmentAfterDoorDate(appointment, door),
        ) || null
    );
};

const EscalationStrategy = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { uid } = useParams();
    const userData = useUserRole();

    const passedReportInfo = location.state?.reportInfo || null;

    const [buildingData, setBuildingData] = useState(passedReportInfo);

    const [doorReports, setDoorReports] = useState([]);

    const [residentialSourceUid, setResidentialSourceUid] = useState(uid || '');

    const [appointmentsByFlat, setAppointmentsByFlat] = useState({});

    const [loading, setLoading] = useState(true);

    const [savingTenant, setSavingTenant] = useState(false);

    const [loadError, setLoadError] = useState('');

    const [tenantError, setTenantError] = useState('');

    const [searchTerm, setSearchTerm] = useState('');

    const [floorFilter, setFloorFilter] = useState('');

    const [statusFilter, setStatusFilter] = useState('');

    const [stepFilter, setStepFilter] = useState('');

    const [showTenantModal, setShowTenantModal] = useState(false);

    const [selectedDoorId, setSelectedDoorId] = useState('');

    const [selectedDoorLabel, setSelectedDoorLabel] = useState('');

    const [selectedDoorSourceUid, setSelectedDoorSourceUid] = useState('');

    const [tenant, setTenant] = useState(EMPTY_TENANT);

    const [showEscalationModal, setShowEscalationModal] = useState(false);

    const [selectedEscalationDoorId, setSelectedEscalationDoorId] = useState('');

    const [selectedEscalationDoorLabel, setSelectedEscalationDoorLabel] =
        useState('');

    const [selectedEscalationSourceUid, setSelectedEscalationSourceUid] =
        useState('');

    const [escalationStepInput, setEscalationStepInput] = useState('Step 1');

    const [escalationNoteInput, setEscalationNoteInput] = useState('');

    const [savingEscalation, setSavingEscalation] = useState(false);

    const [escalationError, setEscalationError] = useState('');

    const [selectedPanelDoorId, setSelectedPanelDoorId] = useState('');

    const [panelError, setPanelError] = useState('');

    const [panelActionLoading, setPanelActionLoading] = useState(false);

    const [showCallModal, setShowCallModal] = useState(false);

    const [callOutcomeInput, setCallOutcomeInput] = useState('No Answer');

    const [callNoteInput, setCallNoteInput] = useState('');

    const [savingCall, setSavingCall] = useState(false);

    const [callError, setCallError] = useState('');

    const [showEstimateModal, setShowEstimateModal] = useState(false);
    const [selectedDoors, setSelectedDoors] = useState(null);
    const [selectedDoor, setSelectedDoor] = useState(null);
    const [isResidentialEstimate, setIsResidentialEstimate] = useState(null);
    const [resi_data, setResiData] = useState([]);

    useEffect(() => {
        const rootElement = document.getElementById('root');

        if (rootElement) {
            Modal.setAppElement(rootElement);
        }
    }, []);

    const fetchBuildingData = useCallback(async () => {
        if (!uid) {
            return null;
        }

        const directReportRef = doc(db, 'Reports', uid);

        const directReportSnapshot = await getDoc(directReportRef);

        if (directReportSnapshot.exists()) {
            return {
                ...(passedReportInfo || {}),

                id: directReportSnapshot.id,

                ...directReportSnapshot.data(),
            };
        }

        const currentReportQuery = query(
            collection(db, 'Reports'),
            where('uid', '==', uid),
        );

        const currentReportSnapshot = await getDocs(currentReportQuery);

        if (!currentReportSnapshot.empty) {
            const reportDocument = currentReportSnapshot.docs[0];

            return {
                ...(passedReportInfo || {}),

                id: reportDocument.id,

                ...reportDocument.data(),
            };
        }

        return passedReportInfo;
    }, [passedReportInfo, uid]);

    // const fetchTenantFromBookings = useCallback(async (buildingName, doorNumber) => {
    //     const cleanBuildingName = normalizeString(buildingName);
    //     const targetFlatKey = getComparableFlatNumber(doorNumber);

    //     if (!cleanBuildingName || !targetFlatKey) {
    //         return null;
    //     }

    //     try {
    //         const inspectionsCol = collection(db, 'inspections');
    //         const inspectionsQuery = query(
    //             inspectionsCol,
    //             where('buildingName', '==', cleanBuildingName),
    //         );
    //         const inspectionsSnapshot = await getDocs(inspectionsQuery);

    //         if (inspectionsSnapshot.empty) {
    //             return null;
    //         }

    //         const matchingBookings = [];

    //         for (const inspectionDocument of inspectionsSnapshot.docs) {
    //             const datesRef = collection(
    //                 db,
    //                 'inspections',
    //                 inspectionDocument.id,
    //                 'dates',
    //             );
    //             const datesSnapshot = await getDocs(datesRef);

    //             for (const dateDocument of datesSnapshot.docs) {
    //                 const bookingsRef = collection(
    //                     db,
    //                     'inspections',
    //                     inspectionDocument.id,
    //                     'dates',
    //                     dateDocument.id,
    //                     'bookings',
    //                 );
    //                 const bookingsSnapshot = await getDocs(bookingsRef);

    //                 bookingsSnapshot.docs.forEach((bookingDocument) => {
    //                     const bookingData = bookingDocument.data();

    //                     const bookingFlat =
    //                         bookingData.flatNo ||
    //                         bookingData.flatNumber ||
    //                         bookingData.doorNumber ||
    //                         bookingData.propertyNumber;

    //                     const bookingFlatKey = getComparableFlatNumber(bookingFlat);

    //                     if (bookingFlatKey === targetFlatKey) {
    //                         matchingBookings.push({
    //                             id: bookingDocument.id,
    //                             dateDocumentId: dateDocument.id,
    //                             ...bookingData,
    //                             date: bookingData.date || dateDocument.id || null,
    //                         });
    //                     }
    //                 });
    //             }
    //         }

    //         if (matchingBookings.length === 0) {
    //             return null;
    //         }

    //         matchingBookings.sort((a, b) => {
    //             const submittedA = parseDate(a.submittedAt) || new Date(0);
    //             const submittedB = parseDate(b.submittedAt) || new Date(0);
    //             return submittedB.getTime() - submittedA.getTime();
    //         });

    //         const latest = matchingBookings[0];

    //         const tenantEmail = latest.email || latest.tenantEmail || '';
    //         const tenantPhone = latest.phone || latest.tenantPhone || '';

    //         return {
    //             ...EMPTY_TENANT,
    //             email: tenantEmail,
    //             phone: tenantPhone,
    //             flatNo: latest.flatNo || doorNumber,
    //             source: 'inspectionsBooking',
    //             bookingDate: latest.date || null,
    //         };
    //     } catch (error) {
    //         console.error(
    //             `Error fetching fallback booking tenant for ${cleanBuildingName} / ${doorNumber}:`,
    //             error,
    //         );
    //         return null;
    //     }
    // }, []);

    const fetchTenantFromBookings = useCallback(async (buildingName, doorNumber) => {
        const cleanBuildingName = normalizeString(buildingName);
        const targetFlatKey = getComparableFlatNumber(doorNumber);

        if (!cleanBuildingName || !targetFlatKey) {
            return null;
        }

        try {
            const inspectionsCol = collection(db, 'inspections');
            const inspectionsQuery = query(
                inspectionsCol,
                where('buildingName', '==', cleanBuildingName),
            );
            const inspectionsSnapshot = await getDocs(inspectionsQuery);

            if (inspectionsSnapshot.empty) {
                return null;
            }

            const matchingBookings = [];
            const buildingWideBookings = [];

            for (const inspectionDocument of inspectionsSnapshot.docs) {
                const datesRef = collection(
                    db,
                    'inspections',
                    inspectionDocument.id,
                    'dates',
                );
                const datesSnapshot = await getDocs(datesRef);

                for (const dateDocument of datesSnapshot.docs) {
                    const bookingsRef = collection(
                        db,
                        'inspections',
                        inspectionDocument.id,
                        'dates',
                        dateDocument.id,
                        'bookings',
                    );
                    const bookingsSnapshot = await getDocs(bookingsRef);

                    bookingsSnapshot.docs.forEach((bookingDocument) => {
                        const bookingData = bookingDocument.data();

                        const bookingFlat =
                            bookingData.flatNo ||
                            bookingData.flatNumber ||
                            bookingData.doorNumber ||
                            bookingData.propertyNumber;

                        const bookingFlatKey = getComparableFlatNumber(bookingFlat);

                        const normalizedBooking = {
                            id: bookingDocument.id,
                            dateDocumentId: dateDocument.id,
                            ...bookingData,
                            date: bookingData.date || dateDocument.id || null,
                        };

                        if (bookingFlatKey === targetFlatKey) {
                            matchingBookings.push(normalizedBooking);
                        }

                        if (
                            normalizeString(bookingData.tenantEmail) ||
                            normalizeString(bookingData.tenantPhone)
                        ) {
                            buildingWideBookings.push(normalizedBooking);
                        }
                    });
                }
            }

            const sortByLatestSubmitted = (a, b) => {
                const submittedA = parseDate(a.submittedAt) || new Date(0);
                const submittedB = parseDate(b.submittedAt) || new Date(0);
                return submittedB.getTime() - submittedA.getTime();
            };

            matchingBookings.sort(sortByLatestSubmitted);
            buildingWideBookings.sort(sortByLatestSubmitted);

            const latestForFlat = matchingBookings[0] || null;
            const latestForBuilding = buildingWideBookings[0] || null;

            if (!latestForFlat && !latestForBuilding) {
                return null;
            }


            const tenantEmail =
                normalizeString(latestForFlat?.email) ||
                normalizeString(latestForBuilding?.tenantEmail) ||
                '';

            const tenantPhone =
                normalizeString(latestForFlat?.phone) ||
                normalizeString(latestForBuilding?.tenantPhone) ||
                '';

            const usedFlatOwnContact =
                normalizeString(latestForFlat?.email) || normalizeString(latestForFlat?.phone);

            return {
                ...EMPTY_TENANT,
                email: tenantEmail,
                phone: tenantPhone,
                flatNo: (latestForFlat || latestForBuilding)?.flatNo || doorNumber,
                source: usedFlatOwnContact
                    ? 'inspectionsBooking'
                    : 'inspectionsBookingLandlordFallback',
                bookingDate: (latestForFlat || latestForBuilding)?.date || null,
            };
        } catch (error) {
            console.error(
                `Error fetching fallback booking tenant for ${cleanBuildingName} / ${doorNumber}:`,
                error,
            );
            return null;
        }
    }, []);


    const fetchResiDoorReports = async () => {
        try {

            if (!currentReport?.buildingName) {
                setResiData([]);
                console.warn("Building name is not available. Cannot fetch residential reports.");
                return;
            }

            const currentBuildingName = currentReport.buildingName;

            const reportsQ = query(
                collection(db, "Reports"),
                where("buildingName", "==", currentBuildingName)
            );
            const reportsSnap = await getDocs(reportsQ);

            const reports = reportsSnap.docs
                .map(doc => ({
                    reportUid: doc.id,
                    reportDate: new Date(doc.data().date || 0),
                }))
                .sort((a, b) => b.reportDate - a.reportDate);

            const allResiReports = [];
            const addedDoorNumbers = new Set();

            for (const { reportUid, reportDate } of reports) {
                const resiSnap = await getDocs(collection(db, "Reports", reportUid, "ResidentialReports"));
                const resiDocs = resiSnap.docs;

                resiDocs.forEach((resiDoc, index) => {
                    const data = resiDoc.data();
                    const doorNumber = data.doorNumber?.trim() || resiDoc.id;

                    if (addedDoorNumbers.has(doorNumber)) return;
                    if (data.requiresReview) return;
                    if (!data.addedBy || data.addedBy.trim() === "") return;

                    const doorUid = resiDoc.id;

                    addedDoorNumbers.add(doorNumber);

                    allResiReports.push({
                        id: doorUid,
                        doorLocation: data.doorLocation,
                        doorLevel: data.doorLevel,
                        doorId: data.doorId,
                        doorCertif: data.doorCertif,
                        doorUsage: data.doorUsage,
                        damageMultiples: data.damageMultiples,
                        damageText: data.damageText,
                        doorCloseAsIntented: data.doorCloseAsIntented,
                        doorStrips: data.doorStrips,
                        fD1: data.fD1,
                        fD2: data.fD2,
                        fD3: data.fD3,
                        fD4: data.fD4,
                        doorDimHeight: data.doorDimHeight,
                        doorDimWidth: data.doorDimWidth,
                        hingesWorking: data.hingesWorking,
                        hingesFireRating: data.hingesFireRating,
                        workNeeded: data.workNeeded,
                        workDone: data.workDone,
                        workDoneUpdatedAt: data.workDoneUpdatedAt,
                        uid: data.uid,
                        date: data.date,
                        doorPullPhotoLink: data.doorPullPhotoLink,
                        doorPushPhotoLink: data.doorPushPhotoLink,
                        doorCertPhotoLink: data.doorCertPhotoLink,
                        hingePhotoLink: data.hingePhotoLink,
                        hingePhotoLink2: data.hingePhotoLink2,
                        hingePhotoLink3: data.hingePhotoLink3,
                        doorDamageLink: data.doorDamageLink,
                        frameMaterial: data.frameMaterial,

                        lockPhotoLink: data.lockPhotoLink,
                        glassFireStampLink: data.glassFireStampLink,
                        letterBoxPhotoLink: data.letterBoxPhotoLink,
                        type: data.type,
                        doorNumber: doorNumber,
                        requiresAttention: data.requiresAttention ?? false,
                        requiresReview: data.requiresReview ?? false,
                        hasAccess: data.hasAccess ?? true,
                        parentReportDate: reportDate,
                    });
                });
            }

            const sortedDoors = allResiReports.sort((a, b) => {
                const dateA = a.parentReportDate?.toMillis?.() ?? Number(a.parentReportDate);
                const dateB = b.parentReportDate?.toMillis?.() ?? Number(b.parentReportDate);

                if (dateB !== dateA) return dateB - dateA;

                return (a.doorNumber || "").localeCompare(b.doorNumber || "", undefined, { numeric: true });
            });

            let fallbackIndex = 1;
            for (const door of sortedDoors) {
                if (!door.doorNumber || door.doorNumber.trim() === "") {
                    door.doorNumber = `${fallbackIndex++}`;
                }
            }

            setResiData(sortedDoors);
        } catch (error) {
            console.error("Error fetching residential reports:", error);
        }
    };

    const fetchTenantForDoor = useCallback(async (sourceUid, doorId, buildingName, doorNumber) => {
        if (!sourceUid || !doorId) {
            return {
                ...EMPTY_TENANT,
            };
        }

        const tenantRef = doc(
            db,
            'Reports',
            sourceUid,
            'ResidentialReports',
            doorId,
            'tenant',
            doorId,
        );

        const tenantSnapshot = await getDoc(tenantRef);

        if (!tenantSnapshot.exists()) {
            const fallbackTenant = await fetchTenantFromBookings(
                buildingName,
                doorNumber || doorId,
            );

            return fallbackTenant || { ...EMPTY_TENANT };
        }

        const tenantData = tenantSnapshot.data();

        const hasRealData = normalizeString(tenantData?.email) || normalizeString(tenantData?.phone);

        if (!hasRealData) {
            const fallbackTenant = await fetchTenantFromBookings(
                buildingName,
                doorNumber || doorId,
            );

            if (fallbackTenant) {
                return fallbackTenant;
            }
        }

        return {
            ...EMPTY_TENANT,
            ...tenantData,

            moveInDate: formatDateForInput(tenantData?.moveInDate),

            escalationHistory: Array.isArray(tenantData?.escalationHistory)
                ? tenantData.escalationHistory
                : [],
        };
    }, [fetchTenantFromBookings]);

    // const fetchTenantForDoor = useCallback(async (sourceUid, doorId) => {
    //     if (!sourceUid || !doorId) {
    //         return {
    //             ...EMPTY_TENANT,
    //         };
    //     }

    //     const tenantRef = doc(
    //         db,
    //         'Reports',
    //         sourceUid,
    //         'ResidentialReports',
    //         doorId,
    //         'tenant',
    //         doorId,
    //     );

    //     const tenantSnapshot = await getDoc(tenantRef);

    //     if (!tenantSnapshot.exists()) {
    //         return {
    //             ...EMPTY_TENANT,
    //         };
    //     }

    //     const tenantData = tenantSnapshot.data();

    //     return {
    //         ...EMPTY_TENANT,
    //         ...tenantData,

    //         moveInDate: formatDateForInput(tenantData?.moveInDate),

    //         escalationHistory: Array.isArray(tenantData?.escalationHistory)
    //             ? tenantData.escalationHistory
    //             : [],
    //     };
    // }, []);

    const fetchLatestAppointments = useCallback(async (buildingName) => {
        const cleanBuildingName = normalizeString(buildingName);

        if (!cleanBuildingName) {
            setAppointmentsByFlat({});
            return {};
        }

        try {
            const inspectionsCol = collection(db, 'inspections');

            const inspectionsQuery = query(
                inspectionsCol,
                where('buildingName', '==', cleanBuildingName),
            );

            const inspectionsSnapshot = await getDocs(inspectionsQuery);

            if (inspectionsSnapshot.empty) {
                setAppointmentsByFlat({});
                return {};
            }

            const allBookings = [];

            for (const inspectionDocument of inspectionsSnapshot.docs) {
                const inspectionData = inspectionDocument.data();

                const datesRef = collection(
                    db,
                    'inspections',
                    inspectionDocument.id,
                    'dates',
                );

                const datesSnapshot = await getDocs(datesRef);

                for (const dateDocument of datesSnapshot.docs) {
                    const dateData = dateDocument.data();

                    const bookingsRef = collection(
                        db,
                        'inspections',
                        inspectionDocument.id,
                        'dates',
                        dateDocument.id,
                        'bookings',
                    );

                    const bookingsSnapshot = await getDocs(bookingsRef);

                    bookingsSnapshot.docs.forEach((bookingDocument) => {
                        const bookingData = bookingDocument.data();

                        allBookings.push({
                            id: bookingDocument.id,

                            inspectionDocumentId: inspectionDocument.id,

                            dateDocumentId: dateDocument.id,

                            inspectionCreatedAt: inspectionData.createdAt || null,

                            inspectionUpdatedAt: inspectionData.updatedAt || null,

                            ...bookingData,

                            date:
                                bookingData.date || dateData.date || dateDocument.id || null,
                        });
                    });
                }
            }

            const bookingsByFlat = {};

            allBookings.forEach((booking) => {
                const flatNumber =
                    booking.flatNo ||
                    booking.flatNumber ||
                    booking.doorNumber ||
                    booking.propertyNumber;

                const flatKey = getComparableFlatNumber(flatNumber);

                if (!flatKey) {
                    return;
                }

                if (!bookingsByFlat[flatKey]) {
                    bookingsByFlat[flatKey] = [];
                }

                bookingsByFlat[flatKey].push(booking);
            });

            Object.keys(bookingsByFlat).forEach((flatKey) => {
                bookingsByFlat[flatKey].sort((bookingA, bookingB) => {
                    const dateA =
                        startOfDay(getAppointmentDateValue(bookingA)) || new Date(0);

                    const dateB =
                        startOfDay(getAppointmentDateValue(bookingB)) || new Date(0);

                    if (dateB.getTime() !== dateA.getTime()) {
                        return dateB.getTime() - dateA.getTime();
                    }

                    const submittedA = parseDate(bookingA.submittedAt) || new Date(0);

                    const submittedB = parseDate(bookingB.submittedAt) || new Date(0);

                    return submittedB.getTime() - submittedA.getTime();
                });
            });

            setAppointmentsByFlat(bookingsByFlat);
            return bookingsByFlat;
        } catch (error) {
            console.error('Error loading inspection appointments:', error);

            setAppointmentsByFlat({});
            return {};
        }
    }, []);

    let currentReport = null;

    const fetchEscalationData = useCallback(async () => {
        if (!uid) {
            setLoadError('Missing report UID.');

            setLoading(false);
            return;
        }

        setLoading(true);
        setLoadError('');

        try {
            currentReport = await fetchBuildingData();

            if (!currentReport?.buildingName) {
                throw new Error('Building report or building name was not found.');
            }

            setBuildingData(currentReport);

            const reportsQuery = query(
                collection(db, 'Reports'),
                where('buildingName', '==', currentReport.buildingName),
            );

            const reportsSnapshot = await getDocs(reportsQuery);

            const reportsForBuilding = reportsSnapshot.docs
                .map((reportDocument) => {
                    const reportData = reportDocument.data();

                    return {
                        documentId: reportDocument.id,

                        reportUid: normalizeString(reportData.uid) || reportDocument.id,

                        reportDate: parseDate(reportData.date) || new Date(0),

                        data: reportData,
                    };
                })
                .sort((reportA, reportB) => {
                    if (reportA.reportUid === uid && reportB.reportUid !== uid) {
                        return -1;
                    }

                    if (reportB.reportUid === uid && reportA.reportUid !== uid) {
                        return 1;
                    }

                    return reportB.reportDate.getTime() - reportA.reportDate.getTime();
                });

            if (!reportsForBuilding.some((report) => report.reportUid === uid)) {
                reportsForBuilding.unshift({
                    documentId: currentReport.id || uid,

                    reportUid: uid,

                    reportDate: parseDate(currentReport.date) || new Date(0),

                    data: currentReport,
                });
            }

            let selectedReportUid = '';

            let selectedReportDate = null;

            let selectedDoorDocuments = [];

            for (const report of reportsForBuilding) {
                if (!report.reportUid) {
                    continue;
                }

                try {
                    const residentialRef = collection(
                        db,
                        'Reports',
                        report.reportUid,
                        'ResidentialReports',
                    );

                    const residentialSnapshot = await getDocs(residentialRef);

                    const validDoorDocuments = residentialSnapshot.docs.filter(
                        (doorDocument) => {
                            const doorData = doorDocument.data();

                            const addedBy = normalizeString(doorData.addedBy);
                            const createdFrom = normalizeString(doorData.createdFrom);

                            if (createdFrom === 'escalation') {
                                return true;
                            }

                            if (!addedBy) {
                                return false;
                            }

                            if (doorData.requiresReview === true) {
                                return false;
                            }

                            return true;
                        },
                    );

                    if (validDoorDocuments.length > 0) {
                        selectedReportUid = report.reportUid;

                        selectedReportDate = report.reportDate;

                        selectedDoorDocuments = validDoorDocuments;

                        break;
                    }
                } catch (reportError) {
                    console.error(
                        `Unable to load doors from report ${report.reportUid}:`,
                        reportError,
                    );
                }
            }

            if (!selectedReportUid || selectedDoorDocuments.length === 0) {
                setDoorReports([]);
                setAppointmentsByFlat({});
                setResidentialSourceUid('');

                setLoadError('No residential doors were found for this building.');

                return;
            }

            setResidentialSourceUid(selectedReportUid);

            const baseDoors = selectedDoorDocuments.map((doorDocument) => ({
                id: doorDocument.id,

                sourceReportUid: selectedReportUid,

                sourceReportDate: selectedReportDate,

                parentReportDate: selectedReportDate,

                ...doorDocument.data(),
            }));

            // const tenantResults = await Promise.allSettled(
            //     baseDoors.map((door) => fetchTenantForDoor(selectedReportUid, door.id)),
            // );
            const tenantResults = await Promise.allSettled(
                baseDoors.map((door) =>
                    fetchTenantForDoor(
                        selectedReportUid,
                        door.id,
                        currentReport.buildingName,
                        normalizeString(door.doorNumber) || door.id,
                    ),
                ),
            );

            const doorsWithTenants = baseDoors.map((door, index) => {
                const tenantResult = tenantResults[index];

                return {
                    ...door,

                    tenant:
                        tenantResult.status === 'fulfilled'
                            ? tenantResult.value
                            : {
                                ...EMPTY_TENANT,
                            },
                };
            });

            const uniqueDoors = [];
            const addedDoorNumbers = new Set();

            doorsWithTenants.forEach((door) => {
                const rawDoorNumber = normalizeString(door.doorNumber) || door.id;

                const comparableDoorNumber = getComparableFlatNumber(rawDoorNumber);

                if (!comparableDoorNumber) {
                    return;
                }

                if (addedDoorNumbers.has(comparableDoorNumber)) {
                    return;
                }

                addedDoorNumbers.add(comparableDoorNumber);

                uniqueDoors.push(door);
            });

            uniqueDoors.sort((doorA, doorB) =>
                getDoorNumber(doorA).localeCompare(getDoorNumber(doorB), undefined, {
                    numeric: true,
                    sensitivity: 'base',
                }),
            );

            // setDoorReports(uniqueDoors);

            const bookingsByFlat = (await fetchLatestAppointments(currentReport.buildingName)) || {};

            const existingFlatKeys = new Set(
                uniqueDoors.map((door) =>
                    getComparableFlatNumber(door.doorNumber || door.id)
                )
            );

            const bookingOnlyDoors = Object.keys(bookingsByFlat)
                .filter((flatKey) => !existingFlatKeys.has(flatKey))
                .map((flatKey) => {
                    const latestBooking = bookingsByFlat[flatKey][0] || {};

                    return {
                        id: `booking-${flatKey}`,
                        sourceReportUid: selectedReportUid,
                        doorNumber:
                            latestBooking.flatNo ||
                            latestBooking.flatNumber ||
                            latestBooking.doorNumber ||
                            latestBooking.propertyNumber ||
                            flatKey,
                        noResidentialRecord: true,
                    };
                });

            const allBaseDoors = [...uniqueDoors, ...bookingOnlyDoors];

            allBaseDoors.sort((doorA, doorB) =>
                getDoorNumber(doorA).localeCompare(getDoorNumber(doorB), undefined, {
                    numeric: true,
                    sensitivity: "base",
                })
            );

            setDoorReports(allBaseDoors);

            await fetchLatestAppointments(currentReport.buildingName);
            await fetchResiDoorReports();
        } catch (error) {
            console.error('Error loading escalation strategy:', error);

            setDoorReports([]);
            setAppointmentsByFlat({});
            setResidentialSourceUid('');

            setLoadError(error?.message || 'Unable to load residential doors.');
        } finally {
            setLoading(false);
        }
    }, [fetchBuildingData, fetchLatestAppointments, fetchTenantForDoor, uid]);

    useEffect(() => {
        fetchEscalationData();

        window.scrollTo({
            top: 0,
            behavior: 'auto',
        });
    }, [fetchEscalationData]);

    const rows = useMemo(() => {
        return doorReports.map((door) => {
            const tenantData = {
                ...EMPTY_TENANT,
                ...(door.tenant || {}),
            };

            const doorNumber = getDoorNumber(door);

            const appointment = getLatestValidAppointment(door, appointmentsByFlat);

            const workCompleted = hasCompletedWork(door);

            const rawHasAccess = normalizeBoolean(door?.hasAccess);

            const accessStatus = getAccessStatus(door);

            const sortedEscalationHistory = getSortedEscalationHistory(tenantData);

            const visibleEscalationHistory = getVisibleEscalationHistory(
                sortedEscalationHistory,
            );

            const latestEscalationEntry = getLatestEscalationEntry(tenantData);

            const latestContactEntry = visibleEscalationHistory[0] || null;

            const escalationStep = getEscalationStep(
                door,
                tenantData,
                accessStatus,
                workCompleted,
                appointment,
                latestEscalationEntry,
            );

            const completedCompany =
                // normalizeString(
                //     door.completedBy
                // ) ||
                // normalizeString(
                //     buildingData
                //         ?.completedBy
                // ) ||
                'FireChecked Ltd';

            const inspectorName =
                // normalizeString(
                //     door.inspector
                // ) ||
                // normalizeString(
                //     door.inspectorName
                // ) ||
                // normalizeString(
                //     door.surveyor
                // ) ||
                // normalizeString(
                //     buildingData
                //         ?.personInCharge
                // ) ||
                normalizeString(buildingData?.completedBy) || '-';

            const appointmentDateValue = getAppointmentDateValue(appointment);

            const lastContactDate = latestContactEntry
                ? formatDateTime(latestContactEntry.date)
                : formatDateTime(
                    door.lastContactDate || door.lastVisitDate || door.updatedAt,
                );

            const lastContactNote = latestContactEntry
                ? normalizeString(latestContactEntry.note) ||
                `Marked as ${normalizeString(
                    getHistoryStepLabel(latestContactEntry),
                )}`
                : normalizeString(door.lastContactNote) ||
                normalizeString(door.contactNote) ||
                '-';

            return {
                id: door.id,

                sourceReportUid: door.sourceReportUid || residentialSourceUid,

                rawHasAccess,
                accessStatus,
                rawDoor: door,

                doorNumber,

                floor: normalizeString(door.doorLevel) || '-',

                tenantName: normalizeString(tenantData.name),

                tenantPhone: normalizeString(tenantData.phone),

                tenantEmail: normalizeString(tenantData.email),

                tenantAddress: normalizeString(tenantData.address),

                tenantMoveInDate: normalizeString(tenantData.moveInDate),

                tenantNotes: normalizeString(tenantData.notes),

                tenantSource:
                    normalizeString(tenantData.source),

                tenant: tenantData,

                escalationHistory: visibleEscalationHistory,

                latestEscalationEntry,

                appointment,

                appointmentDate: appointment ? formatDate(appointmentDateValue) : '-',

                appointmentTime: appointment
                    ? [
                        appointment.firstDate_from ||
                        appointment.fromTime ||
                        appointment.startTime,

                        appointment.firstDate_to ||
                        appointment.toTime ||
                        appointment.endTime,
                    ]
                        .filter(Boolean)
                        .join(' – ') || '-'
                    : '-',

                accessStatus,

                surveyDate: formatDate(getDoorInspectionDateValue(door)),

                workCompleted,

                workCompletedDate: workCompleted
                    ? formatDateTime(door.workDoneUpdatedAt)
                    : '-',

                completedCompany,
                inspectorName,

                lastContactDate,
                lastContactNote,

                escalationStep,

                escalationNote:
                    normalizeString(door.escalationNote) ||
                    normalizeString(door.escalationReason) ||
                    '-',

                nextActionDate: appointment
                    ? formatDate(appointmentDateValue)
                    : formatDate(
                        door.nextActionDate ||
                        door.nextDate ||
                        buildingData?.nextDateResidential,
                    ),

                nextAction: getNextAction(
                    door,
                    accessStatus,
                    escalationStep,
                    workCompleted,
                    appointment,
                ),
            };
        });
    }, [appointmentsByFlat, buildingData, doorReports, residentialSourceUid]);

    const floorOptions = useMemo(() => {
        return [
            ...new Set(
                rows.map((row) => row.floor).filter((floor) => floor && floor !== '-'),
            ),
        ].sort((floorA, floorB) =>
            floorA.localeCompare(floorB, undefined, {
                numeric: true,
                sensitivity: 'base',
            }),
        );
    }, [rows]);

    const statusOptions = useMemo(() => {
        return [
            ...new Set(rows.map((row) => row.accessStatus).filter(Boolean)),
        ].sort();
    }, [rows]);

    const stepOptions = useMemo(() => {
        return [
            ...new Set(rows.map((row) => row.escalationStep).filter(Boolean)),
        ].sort((stepA, stepB) =>
            stepA.localeCompare(stepB, undefined, {
                numeric: true,
                sensitivity: 'base',
            }),
        );
    }, [rows]);

    const filteredRows = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();

        return rows.filter((row) => {
            const searchableValues = [
                row.doorNumber,
                row.floor,
                row.tenantName,
                row.tenantPhone,
                row.tenantEmail,
                row.tenantAddress,
                row.accessStatus,
                row.completedCompany,
                row.inspectorName,
                row.escalationStep,
                row.nextAction,
            ];

            const matchesSearch =
                !search ||
                searchableValues.some((value) =>
                    normalizeString(value).toLowerCase().includes(search),
                );

            const matchesFloor = !floorFilter || row.floor === floorFilter;

            const matchesStatus = !statusFilter || row.accessStatus === statusFilter;

            const matchesStep = !stepFilter || row.escalationStep === stepFilter;

            return matchesSearch && matchesFloor && matchesStatus && matchesStep;
        });
    }, [floorFilter, rows, searchTerm, statusFilter, stepFilter]);

    const summary = useMemo(() => {
        const totalDoors = rows.length;

        const workCompleted = rows.filter((row) => row.workCompleted).length;

        const accessGranted = rows.filter(
            (row) => row.rawHasAccess === true,
        ).length;

        const noAccess = rows.filter((row) => row.rawHasAccess === false).length;

        const appointments = rows.filter((row) => Boolean(row.appointment)).length;

        const step1 = rows.filter((row) => row.escalationStep === 'Step 1').length;

        const step2 = rows.filter((row) => row.escalationStep === 'Step 2').length;

        const step3 = rows.filter((row) => row.escalationStep === 'Step 3').length;

        const completed = rows.filter(
            (row) => row.escalationStep === 'Completed',
        ).length;

        return {
            totalDoors,
            accessGranted,
            noAccess,
            appointments,
            workCompleted,
            step1,
            step2,
            step3,
            completed,
        };
    }, [rows]);

    const getAccessBadgeClass = (status) => {
        switch (status) {
            case "Work Completed":
                return "bg-primary-subtle text-primary border-primary-subtle";

            case "Access Granted":
                return "bg-success-subtle text-success border-success-subtle";

            case "No Access":
                return "bg-danger-subtle text-danger border-danger-subtle";

            case "Not Surveyed":
                return "bg-secondary-subtle text-secondary border-secondary-subtle";

            default:
                return "bg-secondary-subtle text-secondary border-secondary-subtle";
        }
    };

    const getStepBadgeClass = (step) => {
        switch (step) {
            case 'Completed':
                return 'bg-success-subtle text-success border-success-subtle';

            case 'Appointment Booked':
                return 'bg-warning-subtle text-warning-emphasis border-warning-subtle';

            case 'Step 1':
                return 'bg-warning-subtle text-warning-emphasis border-warning-subtle';

            case 'Step 2':
                return 'escalation-step-two';

            case 'Step 3':
                return 'bg-danger text-white border-danger';

            case 'Not Required':
                return 'bg-primary-subtle text-primary border-primary-subtle';

            default:
                return 'bg-secondary-subtle text-secondary border-secondary-subtle';
        }
    };

    const handleOpenTenantModal = (row) => {
        setSelectedDoorId(row.id);

        setSelectedDoorLabel(row.doorNumber);

        setSelectedDoorSourceUid(
            row.sourceReportUid || residentialSourceUid || uid,
        );

        setTenantError('');

        setTenant({
            ...EMPTY_TENANT,
            ...(row.tenant || {}),

            moveInDate: formatDateForInput(row.tenant?.moveInDate),
        });

        setShowTenantModal(true);
    };

    const handleCloseTenantModal = () => {
        if (savingTenant) {
            return;
        }

        setShowTenantModal(false);

        setSelectedDoorId('');
        setSelectedDoorLabel('');
        setSelectedDoorSourceUid('');
        setTenant(EMPTY_TENANT);
        setTenantError('');
    };

    const handleTenantChange = (event) => {
        const { name, value } = event.target;

        setTenant((currentTenant) => ({
            ...currentTenant,
            [name]: value,
        }));
    };

    const handleSaveTenant = async () => {
        const sourceUid = selectedDoorSourceUid || residentialSourceUid || uid;

        if (!sourceUid || !selectedDoorId) {
            setTenantError('Missing report UID or door ID.');

            return;
        }

        setSavingTenant(true);
        setTenantError('');

        try {
            const tenantPayload = {
                name: normalizeString(tenant.name),

                phone: normalizeString(tenant.phone),

                email: normalizeString(tenant.email),

                address: normalizeString(tenant.address),

                moveInDate: normalizeString(tenant.moveInDate),

                notes: normalizeString(tenant.notes),
            };

            // const tenantRef = doc(
            //     db,
            //     'Reports',
            //     sourceUid,
            //     'ResidentialReports',
            //     selectedDoorId,
            //     'tenant',
            //     selectedDoorId,
            // );

            const reportId =
                selectedDoorId?.startsWith('booking-')
                    ? doc(collection(db, 'Reports')).id
                    : selectedDoorId;

            const residentialReportRef = doc(
                db,
                'Reports',
                sourceUid,
                'ResidentialReports',
                reportId
            );

            await setDoc(
                residentialReportRef,
                {
                    doorNumber: selectedDoorLabel.replace(/^R/i, ''),
                    createdFrom: 'escalation',
                },
                { merge: true }
            );

            const tenantRef = doc(
                db,
                'Reports',
                sourceUid,
                'ResidentialReports',
                reportId,
                'tenant',
                reportId
            );

            await setDoc(tenantRef, tenantPayload, {
                merge: true,
            });

            setDoorReports((currentDoors) =>
                currentDoors.map((door) =>
                    door.id === selectedDoorId
                        ? {
                            ...door,
                            tenant: {
                                ...(door.tenant || {}),
                                ...tenantPayload,
                            },
                        }
                        : door,
                ),
            );

            handleCloseTenantModal();
        } catch (error) {
            console.error('Error saving tenant:', error);

            setTenantError(error?.message || 'Unable to save tenant details.');
        } finally {
            setSavingTenant(false);
        }
    };

    const handleOpenEscalationModal = (row) => {
        setSelectedEscalationDoorId(row.id);

        setSelectedEscalationDoorLabel(row.doorNumber);

        setSelectedEscalationSourceUid(
            row.sourceReportUid || residentialSourceUid || uid,
        );

        setEscalationStepInput(row.escalationStep || 'Step 1');

        setEscalationNoteInput('');
        setEscalationError('');
        setShowEscalationModal(true);
    };

    const handleCloseEscalationModal = () => {
        if (savingEscalation) {
            return;
        }

        setShowEscalationModal(false);

        setSelectedEscalationDoorId('');
        setSelectedEscalationDoorLabel('');
        setSelectedEscalationSourceUid('');
        setEscalationStepInput('Step 1');
        setEscalationNoteInput('');
        setEscalationError('');
    };

    const handleSaveEscalation = async () => {
        const sourceUid =
            selectedEscalationSourceUid || residentialSourceUid || uid;

        if (!sourceUid || !selectedEscalationDoorId) {
            setEscalationError('Missing report UID or door ID.');

            return;
        }

        setSavingEscalation(true);
        setEscalationError('');

        try {
            const cleanNote = normalizeString(escalationNoteInput);

            const isSilentRestart = escalationStepInput === 'Step 1' && !cleanNote;

            const entry = isSilentRestart
                ? null
                : {
                    step: escalationStepInput,
                    historyStep: escalationStepInput,
                    date: new Date().toISOString(),
                    note: cleanNote,
                    action: 'Manual Update',
                };

            const tenantRef = doc(
                db,
                'Reports',
                sourceUid,
                'ResidentialReports',
                selectedEscalationDoorId,
                'tenant',
                selectedEscalationDoorId,
            );

            const tenantUpdate = {
                escalationStep: escalationStepInput,
            };

            if (entry) {
                tenantUpdate.escalationHistory = arrayUnion(entry);
            }

            await setDoc(tenantRef, tenantUpdate, { merge: true });

            setDoorReports((currentDoors) =>
                currentDoors.map((door) => {
                    if (door.id !== selectedEscalationDoorId) {
                        return door;
                    }

                    const existingTenant = door.tenant || { ...EMPTY_TENANT };

                    const existingHistory = Array.isArray(
                        existingTenant.escalationHistory,
                    )
                        ? existingTenant.escalationHistory
                        : [];

                    return {
                        ...door,

                        tenant: {
                            ...existingTenant,
                            escalationStep: escalationStepInput,

                            escalationHistory: entry
                                ? [...existingHistory, entry]
                                : existingHistory,
                        },
                    };
                }),
            );

            handleCloseEscalationModal();
        } catch (error) {
            console.error('Error saving escalation step:', error);

            setEscalationError(error?.message || 'Unable to save escalation step.');
        } finally {
            setSavingEscalation(false);
        }
    };

    const persistEscalationEntry = useCallback(
        async (sourceUid, doorId, entry) => {
            const tenantRef = doc(
                db,
                'Reports',
                sourceUid,
                'ResidentialReports',
                doorId,
                'tenant',
                doorId,
            );

            await setDoc(
                tenantRef,
                {
                    escalationHistory: arrayUnion(entry),

                    escalationStep: entry.step,
                },
                { merge: true },
            );

            setDoorReports((currentDoors) =>
                currentDoors.map((door) => {
                    if (door.id !== doorId) {
                        return door;
                    }

                    const existingTenant = door.tenant || { ...EMPTY_TENANT };

                    const existingHistory = Array.isArray(
                        existingTenant.escalationHistory,
                    )
                        ? existingTenant.escalationHistory
                        : [];

                    return {
                        ...door,

                        tenant: {
                            ...existingTenant,
                            escalationStep: entry.step,

                            escalationHistory: [...existingHistory, entry],
                        },
                    };
                }),
            );
        },
        [],
    );

    const panelRow = useMemo(() => {
        return rows.find((row) => row.id === selectedPanelDoorId) || null;
    }, [rows, selectedPanelDoorId]);

    const panelStageIndex = useMemo(() => {
        if (!panelRow) {
            return -1;
        }

        return STEP_SEQUENCE.indexOf(panelRow.escalationStep);
    }, [panelRow]);

    const handleStepLetterAction = async (row, letterKey) => {
        const sourceUid = row.sourceReportUid || residentialSourceUid || uid;

        if (!sourceUid) {
            setPanelError('Missing report UID.');

            return;
        }

        const recipientEmail = normalizeString(row.tenantEmail);

        if (!recipientEmail) {
            setPanelError(`No email address has been saved for ${row.doorNumber}.`);

            return;
        }

        const isInitialLetter = letterKey === 'initial';

        if (!isInitialLetter && letterKey !== 'final') {
            setPanelError('Unknown email type.');

            return;
        }

        const nextStep = isInitialLetter ? 'Step 2' : 'Completed';

        const callableName = isInitialLetter
            ? 'sendInitialContactEmail'
            : 'sendInitialContactEmail';

        const residentName = row.tenantName || 'Resident';

        const completedBy =
            normalizeString(buildingData?.completedBy) || 'FireChecked Ltd';

        setPanelError('');
        setPanelActionLoading(true);

        try {
            const sendEmail = httpsCallable(functions, callableName);

            const result = await sendEmail({
                emails: [row.tenantEmail],
                residentName,
                buildingName: buildingData?.buildingName,
                doorNumber: row.doorNumber,
            });

            const response = result?.data || {};

            if (response?.success === false) {
                throw new Error(
                    response?.message ||
                    response?.error ||
                    'The email could not be sent.',
                );
            }

            const messageId = normalizeString(
                response?.messageId || response?.emailId,
            );

            const entry = {
                step: nextStep,

                historyStep: isInitialLetter ? 'Step 1' : 'Step 3 (Completed)',

                date: new Date().toISOString(),

                note: isInitialLetter
                    ? 'Initial contact email sent'
                    : 'Final escalation email sent',

                action: isInitialLetter ? 'Initial Contact' : 'Final Letter',

                recipientEmail,

                ...(messageId
                    ? {
                        emailMessageId: messageId,
                    }
                    : {}),
            };

            await persistEscalationEntry(sourceUid, row.id, entry);
        } catch (error) {
            console.error(`Error calling ${callableName}:`, error);

            const detailsMessage =
                typeof error?.details === 'string'
                    ? error.details
                    : error?.details?.message;

            setPanelError(
                normalizeString(detailsMessage) ||
                normalizeString(error?.message) ||
                (isInitialLetter
                    ? 'Unable to send the initial contact email.'
                    : 'Unable to send the final escalation email.'),
            );
        } finally {
            setPanelActionLoading(false);
        }
    };

    const handleOpenCallModal = () => {
        setCallOutcomeInput('No Answer');
        setCallNoteInput('');
        setCallError('');
        setShowCallModal(true);
    };

    const handleCloseCallModal = () => {
        if (savingCall) {
            return;
        }

        setShowCallModal(false);
        setCallOutcomeInput('No Answer');
        setCallNoteInput('');
        setCallError('');
    };

    const handleSaveCall = async () => {
        if (!panelRow) {
            setCallError('Select a door first.');

            return;
        }

        const sourceUid = panelRow.sourceReportUid || residentialSourceUid || uid;

        if (!sourceUid) {
            setCallError('Missing report UID.');

            return;
        }

        setSavingCall(true);
        setCallError('');

        try {
            const entry = {
                step: 'Step 3',
                historyStep: 'Step 2',

                date: new Date().toISOString(),

                note: normalizeString(callNoteInput)
                    ? `${callOutcomeInput} — ${normalizeString(callNoteInput)}`
                    : callOutcomeInput,

                action: 'Phone Call',
                outcome: callOutcomeInput,
            };

            await persistEscalationEntry(sourceUid, panelRow.id, entry);

            handleCloseCallModal();
        } catch (error) {
            console.error('Error saving call log:', error);

            setCallError(error?.message || 'Unable to save the call log.');
        } finally {
            setSavingCall(false);
        }
    };

    const handleOpenDoor = (row) => {
        const sourceUid = row.sourceReportUid || residentialSourceUid || uid;

        const doorUrl = `/doors/${sourceUid}/resi_details/${row.id}`;

        window.open(doorUrl, '_blank', 'noopener,noreferrer');
    };

    const buildingAddress = [
        buildingData?.buildingAddress || buildingData?.clientAddress,

        buildingData?.buildingCity || buildingData?.city,

        buildingData?.buildingPostcode || buildingData?.postcode,
    ]
        .filter(Boolean)
        .join(', ');

    return (
        <div className="escalation-strategy-page">
            <div className="container-fluid py-4 px-3 px-lg-4">
                <div className="escalation-page-container">
                    <div className="escalation-page-header mb-4">
                        <button
                            type="button"
                            className="btn btn-link text-danger fw-semibold text-decoration-none p-0"
                            onClick={() => navigate("/reports")}
                        >
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="me-2"
                            >
                                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                            </svg>
                            Back to Reports
                        </button>

                        <div className="text-center">
                            <h1 className="page-title mb-1">Escalation Strategy</h1>

                            <p className="text-muted mb-0">
                                Track access, appointments and completed works
                            </p>
                        </div>

                        <div />
                    </div>

                    {loadError && (
                        <div className="alert alert-danger" role="alert">
                            {loadError}
                        </div>
                    )}

                    <div className="overview-layout mb-3">
                        <div className="card building-overview-card shadow-sm">
                            <div className="building-card-layout">
                                <div className="building-image-section">
                                    {buildingData?.buildingLink ? (
                                        <img
                                            src={buildingData.buildingLink}
                                            alt={
                                                buildingData?.buildingName
                                                    ? `${buildingData.buildingName} building`
                                                    : 'Building'
                                            }
                                            className="building-main-image"
                                        />
                                    ) : (
                                        <div className="building-image-placeholder">
                                            <small>No building image</small>
                                        </div>
                                    )}

                                    {buildingData?.logo && (
                                        <div className="building-logo-wrapper">
                                            <img
                                                src={buildingData.logo}
                                                alt="Company logo"
                                                className="building-logo"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="building-content-section">
                                    <div className="building-title-section">
                                        <div>
                                            <span className="building-eyebrow">
                                                Residential Building
                                            </span>

                                            <h4 className="building-title">
                                                {buildingData?.buildingName || 'Building'}
                                            </h4>

                                            <p className="building-address">
                                                {buildingAddress || '-'}
                                            </p>
                                        </div>

                                        <div className="building-report-id">
                                            <span>Report ID</span>

                                            <strong>{buildingData?.reportId || '-'}</strong>
                                        </div>
                                    </div>

                                    <div className="building-details-grid">
                                        <BuildingInfo
                                            label="Building Type"
                                            value={buildingData?.buildingType}
                                        />

                                        <BuildingInfo label="Floors" value={buildingData?.floors} />

                                        <BuildingInfo
                                            label="Client"
                                            value={buildingData?.clientName}
                                        />

                                        <BuildingInfo
                                            label="Responsible Person"
                                            value={buildingData?.personInCharge}
                                        />

                                        <BuildingInfo
                                            label="Planned Visit"
                                            value={formatDate(buildingData?.date)}
                                        />

                                        <BuildingInfo
                                            label="Inspected By"
                                            value={buildingData?.completedBy || 'FireChecked Ltd'}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="summary-cards-grid">
                            <SummaryCard
                                title="Access Granted"
                                value={summary.accessGranted}
                                percentage={calculatePercentage(
                                    summary.accessGranted,
                                    summary.totalDoors,
                                )}
                                note="Passed or failed surveys"
                                className="summary-card-green summary-position-access"
                            />

                            <SummaryCard
                                title="No Access"
                                value={summary.noAccess}
                                percentage={calculatePercentage(
                                    summary.noAccess,
                                    summary.totalDoors,
                                )}
                                note="Requires escalation"
                                className="summary-card-red summary-position-no-access"
                            />

                            <SummaryCard
                                title="Appointments"
                                value={summary.appointments}
                                percentage={calculatePercentage(
                                    summary.appointments,
                                    summary.totalDoors,
                                )}
                                note="Latest inspections bookings"
                                className="summary-card-yellow summary-position-appointments"
                            />

                            <SummaryCard
                                title="Work Completed"
                                value={summary.workCompleted}
                                percentage={calculatePercentage(
                                    summary.workCompleted,
                                    summary.totalDoors,
                                )}
                                note="Work completed"
                                className="summary-card-purple summary-position-work"
                            />

                            <SummaryCard
                                title="Total Doors"
                                value={summary.totalDoors}
                                note={`${floorOptions.length} floor${floorOptions.length === 1 ? '' : 's'
                                    }`}
                                className="summary-card-slate summary-position-total"
                            />
                        </div>
                    </div>

                    <div className="table-and-sidebar">
                        <div className="card border-0 shadow-sm overflow-hidden">
                            <div className="card-body border-bottom p-3">
                                <div className="row g-2">
                                    <div className="row g-2 align-items-center flex-nowrap">
                                        <div className="col-12 col-lg-5">
                                            <input
                                                type="search"
                                                className="form-control"
                                                placeholder="Search door, tenant, phone or email..."
                                                value={searchTerm}
                                                onChange={(event) => setSearchTerm(event.target.value)}
                                            />
                                        </div>

                                        <div className="col">
                                            <select
                                                className="form-select"
                                                value={floorFilter}
                                                onChange={(e) => setFloorFilter(e.target.value)}
                                            >
                                                <option value="">All Floors</option>
                                                {floorOptions.map((floor) => (
                                                    <option key={floor} value={floor}>
                                                        {floor}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col">
                                            <select
                                                className="form-select"
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                            >
                                                <option value="">All Status</option>
                                                {statusOptions.map((status) => (
                                                    <option key={status} value={status}>
                                                        {status}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col">
                                            <select
                                                className="form-select"
                                                value={stepFilter}
                                                onChange={(e) => setStepFilter(e.target.value)}
                                            >
                                                <option value="">All Steps</option>
                                                {stepOptions.map((step) => (
                                                    <option key={step} value={step}>
                                                        {step}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-auto">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                disabled={loading}
                                                onClick={fetchEscalationData}
                                            >
                                                Refresh
                                            </button>
                                        </div>

                                        {(userData.role === 'admin' || userData.role === 'manager' || userData.role === 'inspection' || userData.role === 'coordinator' || userData.role === 'remedial') && (
                                            <div className="col-auto">
                                                <button
                                                    className="btn btn-info"
                                                    onClick={() => {
                                                        setSelectedDoors(resi_data);
                                                        setShowEstimateModal(true);
                                                        setIsResidentialEstimate(true);
                                                        setSelectedDoor(null);
                                                    }}
                                                    disabled={loading}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                                    Estimate
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive escalation-table-wrapper">
                                <table className="table table-hover table-bordered align-middle text-center mb-0 escalation-table">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Flat</th>

                                            <th>Floor</th>

                                            <th>Tenant</th>

                                            <th>Status</th>

                                            <th>Appointment</th>

                                            <th>Work Inspected By</th>

                                            <th>Last Contact</th>

                                            <th>Escalation</th>

                                            <th>Next Action</th>

                                            <th>Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="10" className="py-5">
                                                    <div
                                                        className="spinner-border text-danger"
                                                        role="status"
                                                    />

                                                    <div className="text-muted mt-3">
                                                        Loading doors...
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : filteredRows.length > 0 ? (
                                            filteredRows.map((row) => (
                                                <tr key={row.id}>
                                                    <td>
                                                        <strong className="door-number-label">
                                                            {row.doorNumber}
                                                        </strong>
                                                    </td>

                                                    <td>{row.floor}</td>

                                                    <td>
                                                        {!row.tenantName && !row.tenantPhone && !row.tenantEmail ? (
                                                            <div className="text-muted fw-bold">No tenant details</div>
                                                        ) : (
                                                            <>
                                                                <div className="fw-semibold">
                                                                    {row.tenantName || <span className="text-muted small fst-italic">No name</span>}
                                                                </div>

                                                                {row.tenantSource === 'inspectionsBookingLandlordFallback' && (
                                                                    <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle small mb-1">
                                                                        Landlord contact
                                                                    </span>
                                                                )}

                                                                {row.tenantPhone ? (
                                                                    <small className="d-block text-muted">
                                                                        {row.tenantPhone}
                                                                    </small>
                                                                ) : (
                                                                    <small className="d-block text-muted fst-italic">No phone</small>
                                                                )}

                                                                {row.tenantEmail ? (
                                                                    <small className="d-block text-muted text-break">
                                                                        {row.tenantEmail}
                                                                    </small>
                                                                ) : (
                                                                    <small className="d-block text-muted fst-italic text-break">No email</small>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>

                                                    <td>
                                                        <span
                                                            className={`badge border ${getAccessBadgeClass(
                                                                row.accessStatus,
                                                            )}`}
                                                        >
                                                            {row.accessStatus}
                                                        </span>

                                                        <small className="d-block text-muted mt-1">
                                                            {row.surveyDate}
                                                        </small>
                                                    </td>

                                                    <td>
                                                        {row.appointment ? (
                                                            <>
                                                                <span className="badge border bg-warning-subtle text-warning-emphasis border-warning-subtle">
                                                                    Booked
                                                                </span>

                                                                <small className="d-block text-muted mt-1">
                                                                    {row.appointmentDate}
                                                                </small>

                                                                <small className="d-block text-muted">
                                                                    {row.appointmentTime}
                                                                </small>
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>

                                                    <td>
                                                        {/* {row.workCompleted ? (
                                                            <span className="badge border bg-primary-subtle text-primary border-primary-subtle mb-2">
                                                                Work
                                                                Completed
                                                            </span>
                                                        ) : (
                                                            <span className="badge border bg-secondary-subtle text-secondary border-secondary-subtle mb-2">
                                                                Not
                                                                Completed
                                                            </span>
                                                        )} */}

                                                        <div className="fw-semibold">
                                                            {row.completedCompany}
                                                        </div>

                                                        <small className="d-block text-muted">
                                                            Inspector: {row.inspectorName}
                                                        </small>

                                                        {row.workCompleted && (
                                                            <small className="d-block text-muted mt-1">
                                                                {row.workCompletedDate}
                                                            </small>
                                                        )}
                                                    </td>

                                                    <td>
                                                        <div>{row.lastContactDate}</div>

                                                        <small className="text-muted">
                                                            {row.lastContactNote}
                                                        </small>
                                                    </td>

                                                    <td>
                                                        <span
                                                            className={`badge border ${getStepBadgeClass(
                                                                row.escalationStep,
                                                            )}`}
                                                        >
                                                            {row.escalationStep}
                                                        </span>

                                                        <small className="d-block text-muted mt-1">
                                                            {row.escalationNote}
                                                        </small>
                                                    </td>

                                                    <td>
                                                        <div>{row.nextAction}</div>

                                                        <small className="text-muted">
                                                            {row.nextActionDate}
                                                        </small>
                                                    </td>

                                                    <td>
                                                        <div className="d-flex justify-content-center gap-2 flex-wrap">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => handleOpenEscalationModal(row)}
                                                            >
                                                                Escalation
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => handleOpenTenantModal(row)}
                                                            >
                                                                Tenant
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={() => handleOpenDoor(row)}
                                                            >
                                                                View Door
                                                            </button>

                                                            {(userData.role === 'admin' || userData.role === 'manager' || userData.role === 'inspection' || userData.role === 'coordinator' || userData.role === 'remedial') && (
                                                                <button
                                                                    className="btn btn-primary btn-sm d-flex align-items-center gap-2 rounded-pill"
                                                                    style={{
                                                                        fontSize: '11px',
                                                                        padding: '6px 14px',
                                                                        whiteSpace: 'nowrap',
                                                                    }} G
                                                                    onClick={() => {
                                                                        setSelectedDoor(row.rawDoor);
                                                                        setShowEstimateModal(true);
                                                                        setSelectedDoors(null);
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faDownload} />
                                                                    Estimate
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="10" className="text-muted py-5">
                                                    No residential doors found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* <div className="card-footer bg-light d-flex justify-content-between flex-wrap gap-2">
                                <small className="text-muted">
                                    Residential
                                    source:{" "}
                                    {residentialSourceUid ===
                                        uid
                                        ? "Current report"
                                        : "Previous report containing residential doors"}
                                </small>

                                <small className="fw-semibold">
                                    Showing{" "}
                                    {
                                        filteredRows.length
                                    }{" "}
                                    of{" "}
                                    {rows.length}{" "}
                                    doors
                                </small>
                            </div> */}
                        </div>

                        <div className="escalation-sidebar">
                            <div className="card escalation-panel-card shadow-sm">
                                <div className="escalation-panel-header py-2">
                                    Escalation Strategy (3 Step)
                                </div>

                                <div className="escalation-panel-body py-2">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <label
                                            className="form-label small text-muted mb-0 flex-shrink-0"
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            Select a door
                                        </label>

                                        <select
                                            className="form-select form-select-sm"
                                            style={{ minHeight: 'auto', height: '28px', padding: '2px 8px', fontSize: '0.78rem' }}
                                            value={selectedPanelDoorId}
                                            onChange={(event) => {
                                                setSelectedPanelDoorId(event.target.value);
                                                setPanelError('');
                                            }}
                                        >
                                            <option value="">Choose a flat...</option>

                                            {rows.map((row) => (
                                                <option key={row.id} value={row.id}>
                                                    {row.doorNumber} — {row.tenantName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {panelError && (
                                        <div className="alert alert-danger py-1 px-2 small mb-2">
                                            {panelError}
                                        </div>
                                    )}

                                    <div className="escalation-panel-scroll">
                                        {!panelRow ? (
                                            <small className="text-muted d-block py-1">
                                                Select a door above to walk through its escalation
                                                steps.
                                            </small>
                                        ) : panelStageIndex === -1 ? (
                                            <small className="text-muted d-block py-1">
                                                {panelRow.doorNumber} is currently marked as "
                                                {panelRow.escalationStep}" and isn't in the 3-step
                                                no-access flow. Use the Escalation button in the table
                                                to change it manually.
                                            </small>
                                        ) : (
                                            <EscalationStepTracker
                                                row={panelRow}
                                                stageIndex={panelStageIndex}
                                                actionLoading={panelActionLoading}
                                                onDownloadInitialLetter={() =>
                                                    handleStepLetterAction(panelRow, 'initial')
                                                }
                                                onLogCall={handleOpenCallModal}
                                                onDownloadFinalLetter={() =>
                                                    handleStepLetterAction(panelRow, 'final')
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <EscalationSummaryCard summary={summary} />
                        </div>
                    </div>
                </div>
            </div>

            <EstimateModal
                show={showEstimateModal}
                onHide={() => {
                    setShowEstimateModal(false);
                    setSelectedDoors([]);
                    setIsResidentialEstimate(false);
                }}
                doorData={selectedDoor}
                doorsData={selectedDoors}
                buildingData={{
                    ...buildingData,
                    uid: buildingData?.originalUid || buildingData?.uid
                }}
                isResidential={isResidentialEstimate}
                onGenerate={() => {
                }}
            />

            <Modal
                isOpen={showTenantModal}
                onRequestClose={handleCloseTenantModal}
                shouldCloseOnOverlayClick={!savingTenant}
                shouldCloseOnEsc={!savingTenant}
                className="tenant-react-modal"
                overlayClassName="tenant-react-modal-overlay"
                contentLabel="Tenant Details"
            >
                <div className="d-flex justify-content-between align-items-center border-bottom p-3">
                    <h5 className="mb-0">Tenant Details — {selectedDoorLabel}</h5>

                    <button
                        type="button"
                        className="btn-close"
                        disabled={savingTenant}
                        onClick={handleCloseTenantModal}
                    />
                </div>

                <div className="p-3">
                    {tenantError && (
                        <div className="alert alert-danger">{tenantError}</div>
                    )}

                    <div className="row">
                        <TenantInput
                            label="Full Name"
                            name="name"
                            value={tenant.name}
                            disabled={savingTenant}
                            onChange={handleTenantChange}
                        />

                        <TenantInput
                            label="Phone Number"
                            name="phone"
                            type="tel"
                            value={tenant.phone}
                            disabled={savingTenant}
                            onChange={handleTenantChange}
                        />

                        <TenantInput
                            label="Email Address"
                            name="email"
                            type="email"
                            value={tenant.email}
                            disabled={savingTenant}
                            onChange={handleTenantChange}
                        />

                        <TenantInput
                            label="Move-in Date"
                            name="moveInDate"
                            type="date"
                            value={tenant.moveInDate}
                            disabled={savingTenant}
                            onChange={handleTenantChange}
                        />

                        <TenantInput
                            label="Address"
                            name="address"
                            value={tenant.address}
                            disabled={savingTenant}
                            onChange={handleTenantChange}
                        />
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Notes</label>

                            <textarea
                                name="notes"
                                rows={3}
                                className="form-control"
                                style={{ resize: 'none' }}
                                value={tenant.notes}
                                disabled={savingTenant}
                                onChange={handleTenantChange}
                            />
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2 border-top p-3">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={savingTenant}
                        onClick={handleCloseTenantModal}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={savingTenant}
                        onClick={handleSaveTenant}
                    >
                        {savingTenant ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={showEscalationModal}
                onRequestClose={handleCloseEscalationModal}
                shouldCloseOnOverlayClick={!savingEscalation}
                shouldCloseOnEsc={!savingEscalation}
                className="tenant-react-modal"
                overlayClassName="tenant-react-modal-overlay"
                contentLabel="Escalation Step"
            >
                <div className="d-flex justify-content-between align-items-center border-bottom p-3">
                    <h5 className="mb-0">
                        Update Escalation — {selectedEscalationDoorLabel}
                    </h5>

                    <button
                        type="button"
                        className="btn-close"
                        disabled={savingEscalation}
                        onClick={handleCloseEscalationModal}
                    />
                </div>

                <div className="p-3">
                    {escalationError && (
                        <div className="alert alert-danger">{escalationError}</div>
                    )}

                    <div className="mb-3">
                        <label className="form-label">Escalation Step</label>

                        <select
                            className="form-select"
                            value={escalationStepInput}
                            disabled={savingEscalation}
                            onChange={(event) => setEscalationStepInput(event.target.value)}
                        >
                            {ESCALATION_STEP_OPTIONS.map((step) => (
                                <option key={step} value={step}>
                                    {step}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Contact Note</label>

                        <textarea
                            rows="3"
                            className="form-control"
                            placeholder="e.g. Called tenant, left voicemail"
                            style={{ resize: 'none' }}
                            value={escalationNoteInput}
                            disabled={savingEscalation}
                            onChange={(event) => setEscalationNoteInput(event.target.value)}
                        />
                    </div>

                    {selectedEscalationDoorId && (
                        <div>
                            <label className="form-label">History</label>

                            <div
                                className="border rounded p-2"
                                style={{
                                    maxHeight: '160px',
                                    overflowY: 'auto',
                                }}
                            >
                                {(
                                    rows.find((row) => row.id === selectedEscalationDoorId)
                                        ?.escalationHistory || []
                                ).length === 0 ? (
                                    <small className="text-muted">
                                        No escalation history yet.
                                    </small>
                                ) : (
                                    rows
                                        .find((row) => row.id === selectedEscalationDoorId)
                                        ?.escalationHistory.map((entry, index) => (
                                            <div
                                                key={index}
                                                className="d-flex justify-content-between border-bottom py-1"
                                            >
                                                <small>
                                                    <strong>{getHistoryStepLabel(entry)}</strong>{' '}
                                                    {entry.note && `— ${entry.note}`}
                                                </small>

                                                <small className="text-muted">
                                                    {formatDateTime(entry.date)}
                                                </small>
                                            </div>
                                        ))
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="d-flex justify-content-end gap-2 border-top p-3">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={savingEscalation}
                        onClick={handleCloseEscalationModal}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="btn btn-danger"
                        disabled={savingEscalation}
                        onClick={handleSaveEscalation}
                    >
                        {savingEscalation ? 'Saving...' : 'Save Escalation'}
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={showCallModal}
                onRequestClose={handleCloseCallModal}
                shouldCloseOnOverlayClick={!savingCall}
                shouldCloseOnEsc={!savingCall}
                className="tenant-react-modal call-log-modal"
                overlayClassName="tenant-react-modal-overlay"
                contentLabel="Log Phone Call"
            >
                <div className="d-flex justify-content-between align-items-center border-bottom p-3">
                    <h5 className="mb-0">Log Phone Call — {panelRow?.doorNumber}</h5>

                    <button
                        type="button"
                        className="btn-close"
                        disabled={savingCall}
                        onClick={handleCloseCallModal}
                    />
                </div>

                <div className="p-3">
                    {callError && <div className="alert alert-danger">{callError}</div>}

                    <div className="mb-3">
                        <label className="form-label">Outcome</label>

                        <select
                            className="form-select"
                            value={callOutcomeInput}
                            disabled={savingCall}
                            onChange={(event) => setCallOutcomeInput(event.target.value)}
                        >
                            <option value="Answered">Answered</option>

                            <option value="No Answer">No Answer</option>

                            <option value="Left Voicemail">Left Voicemail</option>

                            <option value="Number Unreachable">Number Unreachable</option>

                            <option value="Call Back Requested">Call Back Requested</option>
                        </select>
                    </div>

                    <div className="mb-1">
                        <label className="form-label">
                            Notes — what happened on the call
                        </label>

                        <textarea
                            rows="4"
                            className="form-control"
                            placeholder="e.g. Spoke to tenant, agreed to book an appointment for next Tuesday"
                            value={callNoteInput}
                            disabled={savingCall}
                            onChange={(event) => setCallNoteInput(event.target.value)}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2 border-top p-3">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={savingCall}
                        onClick={handleCloseCallModal}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="btn btn-danger"
                        disabled={savingCall}
                        onClick={handleSaveCall}
                    >
                        {savingCall ? 'Saving...' : 'Save Call Log & Advance'}
                    </button>
                </div>
            </Modal>
        </div>
    );
};

const BuildingInfo = ({ label, value }) => (
    <div className="building-info-item">
        <span className="building-info-label">{label}</span>

        <span className="building-info-value">{value || '-'}</span>
    </div>
);

const EscalationSummaryCard = ({ summary }) => {
    const escalationRows = [
        {
            label: 'Step 1',
            value: summary.step1,
            dotClass: 'escalation-summary-dot-step1',
        },
        {
            label: 'Step 2',
            value: summary.step2,
            dotClass: 'escalation-summary-dot-step2',
        },
        {
            label: 'Step 3',
            value: summary.step3,
            dotClass: 'escalation-summary-dot-step3',
        },
        {
            label: 'Completed',
            value: summary.completed,
            dotClass: 'escalation-summary-dot-completed',
        },
    ];

    return (
        <div className="card escalation-summary-card shadow-sm">
            <div className="escalation-summary-header">Escalation Summary</div>

            <div className="escalation-summary-list">
                {escalationRows.map((row) => (
                    <div key={row.label} className="escalation-summary-row">
                        <span className={`escalation-summary-dot ${row.dotClass}`} />

                        <span className="escalation-summary-label">{row.label}</span>

                        <span className="escalation-summary-value">{row.value} flats</span>
                    </div>
                ))}
            </div>

            <div className="escalation-summary-total">
                <span>Total</span>

                <strong>{summary.totalDoors} flats</strong>
            </div>
        </div>
    );
};

const EscalationStepTracker = ({
    row,
    stageIndex,
    actionLoading,
    onDownloadInitialLetter,
    onLogCall,
    onDownloadFinalLetter,
}) => {
    const ascendingHistory = getAscendingEscalationHistory(row.escalationHistory);

    const step1CompletedEntry = findStepTransitionEntry(
        row.escalationHistory,
        'Step 2',
    );

    const step2CompletedEntry = findStepTransitionEntry(
        row.escalationHistory,
        'Step 3',
    );

    const step3CompletedEntry = findStepTransitionEntry(
        row.escalationHistory,
        'Completed',
    );

    const steps = [
        {
            number: 1,
            title: 'Initial Contact',
            subtitle: 'Email',
            done: stageIndex >= 1,
            active: stageIndex === 0,
            completedEntry: step1CompletedEntry,
            dotClass: 'escalation-summary-dot-step1',
            action: (
                <button
                    type="button"
                    className="btn btn-outline-warning btn-sm"
                    disabled={stageIndex !== 0 || actionLoading}
                    onClick={onDownloadInitialLetter}
                >
                    Send Email
                </button>
            ),
        },
        {
            number: 2,
            title: 'Phone Call',
            subtitle: 'Follow up call attempt',
            done: stageIndex >= 2,
            active: stageIndex === 1,
            completedEntry: step2CompletedEntry,
            dotClass: 'escalation-summary-dot-step2',
            action: (
                <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={stageIndex !== 1 || actionLoading}
                    onClick={onLogCall}
                >
                    Log Call
                </button>
            ),
        },
        {
            number: 3,
            title: 'Final Letter',
            subtitle: 'Formal letter sent',
            done: stageIndex >= 3,
            active: stageIndex === 2,
            completedEntry: step3CompletedEntry,
            dotClass: 'escalation-summary-dot-step3',
            action: (
                <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    disabled={stageIndex !== 2 || actionLoading}
                    onClick={onDownloadFinalLetter}
                >
                    Download Letter
                </button>
            ),
        },
    ];

    return (
        <div>
            <div className="escalation-tracker">
                {steps.map((step) => (
                    <div
                        key={step.number}
                        className={`escalation-tracker-row ${step.done ? 'is-done' : step.active ? 'is-active' : 'is-pending'
                            }`}
                    >
                        <div className="escalation-tracker-bullet">
                            {step.done ? '✓' : step.number}
                        </div>

                        <div className="escalation-tracker-body">
                            <div className="escalation-tracker-title">{step.title}</div>

                            <div className="escalation-tracker-subtitle">{step.subtitle}</div>

                            {step.done && step.completedEntry && (
                                <div className="escalation-tracker-completed-note">
                                    {formatDateTime(step.completedEntry.date)}

                                    {step.completedEntry.note && ` — ${step.completedEntry.note}`}
                                </div>
                            )}

                            <div className="mt-2">{step.action}</div>
                        </div>
                    </div>
                ))}
            </div>

            {ascendingHistory.length > 0 && (
                <div>
                    <div className="escalation-tracker-history-header">
                        <span>Full History</span>

                        <span className="escalation-tracker-history-count">
                            {ascendingHistory.length}
                        </span>
                    </div>

                    <div className="escalation-tracker-history">
                        {[...ascendingHistory].reverse().map((entry, index) => (
                            <div
                                key={index}
                                className="escalation-tracker-history-row"
                                title={`${getHistoryStepLabel(entry)}${entry.note ? ' — ' + entry.note : ''
                                    }`}
                            >
                                <span
                                    className={`escalation-tracker-history-dot ${getHistoryDotClass(
                                        entry,
                                    )}`}
                                />

                                <span className="escalation-tracker-history-text">
                                    <strong>{getHistoryStepLabel(entry)}</strong>

                                    {entry.note && ` — ${entry.note}`}
                                </span>

                                <span className="escalation-tracker-history-date">
                                    {formatShortDateTime(entry.date)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const SummaryCard = ({ title, value, percentage, note, className }) => (
    <div className={`summary-card-item ${className}`}>
        <div className="summary-card-header">
            <span className="summary-card-title">{title}</span>

            {percentage && (
                <span className="summary-card-percentage">{percentage}</span>
            )}
        </div>

        <div className="summary-card-value">{value}</div>

        <div className="summary-card-note">{note}</div>
    </div>
);

const TenantInput = ({
    label,
    name,
    value,
    onChange,
    disabled,
    type = 'text',
}) => (
    <div className="col-md-6 mb-3">
        <label className="form-label">{label}</label>

        <input
            type={type}
            name={name}
            className="form-control"
            value={value}
            disabled={disabled}
            onChange={onChange}
        />
    </div>
);

export default EscalationStrategy;
